import { createFileRoute } from '@tanstack/react-router'
import { Plus, ArrowDownCircle, ArrowUpCircle, Filter, Wallet } from 'lucide-react'
import { useState } from 'react'

import {
  EmptyState,
  NoResultsEmptyState,
  determineEmptyStateType,
} from '@/components/common/empty-state'
import { ExportButton } from '@/components/common/export-button'
import { CashFlowForm } from '@/components/finance/cash-flow/cash-flow-form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAccounts, useCategories, useConfirmDialog, useCurrencyFormatter } from '@/hooks'
import {
  useCashFlows,
  useDeleteCashFlow,
  type CashFlowListParams,
  type CashFlowWithRelations,
  type CashFlowType,
} from '@/hooks'

export const Route = createFileRoute('/_authenticated/finance/cash-flows/' as never)({
  component: CashFlowListPage,
})



function CashFlowListPage() {
  const [filters, setFilters] = useState<CashFlowListParams>({ page: 1, pageSize: 20 })
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingCashFlow, setEditingCashFlow] = useState<CashFlowWithRelations | null>(null)
  const [showFilters, setShowFilters] = useState(false)

  const { data, isLoading, error } = useCashFlows(filters)
  const { data: accounts } = useAccounts()
  const { data: categories } = useCategories()
  const deleteMutation = useDeleteCashFlow()
  const { formatMoney } = useCurrencyFormatter()
  const { confirmWithPreset, ConfirmDialog, setLoading } = useConfirmDialog()

  const handleDelete = async (id: string) => {
    const confirmed = await confirmWithPreset(
      'delete',
      '确定要删除此现金流记录吗？此操作不可恢复。'
    )
    if (confirmed) {
      setLoading(true)
      try {
        await deleteMutation.mutateAsync(id)
      } finally {
        setLoading(false)
      }
    }
  }

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }))
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-destructive">加载失败: {error.message}</div>
      </div>
    )
  }

  const cashFlows = data?.data ?? []
  const meta = data?.meta

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">现金流管理</h1>
          <p className="text-muted-foreground">记录和管理公司收支</p>
        </div>
        <div className="flex gap-2">
          <ExportButton
            type="cash_flows"
            filters={{
              accountId: filters.accountId,
              type: filters.type,
              startDate: filters.startDate,
              endDate: filters.endDate,
            }}
          />
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            <Filter className="h-4 w-4" />
            筛选
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            新建记录
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="rounded-lg border bg-muted/30 p-4">
          <div className="grid gap-4 md:grid-cols-4">
            <div>
              <label className="mb-1 block text-sm font-medium">账户</label>
              <Select
                value={filters.accountId ?? 'all'}
                onValueChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    accountId: value === 'all' ? undefined : value,
                    page: 1,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="全部账户" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部账户</SelectItem>
                  {accounts?.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">类型</label>
              <Select
                value={filters.type ?? 'all'}
                onValueChange={(value) =>
                  setFilters((prev) => ({
                    ...prev,
                    type: value === 'all' ? undefined : (value as CashFlowType),
                    page: 1,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="全部类型" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">全部类型</SelectItem>
                  <SelectItem value="income">收入</SelectItem>
                  <SelectItem value="expense">支出</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">开始日期</label>
              <input
                type="date"
                value={filters.startDate ?? ''}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    startDate: e.target.value || undefined,
                    page: 1,
                  }))
                }
                className="w-full rounded-md border px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">结束日期</label>
              <input
                type="date"
                value={filters.endDate ?? ''}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    endDate: e.target.value || undefined,
                    page: 1,
                  }))
                }
                className="w-full rounded-md border px-3 py-2 text-sm"
              />
            </div>
          </div>
        </div>
      )}

      {(showCreateForm || editingCashFlow) && (
        <CashFlowForm
          cashFlow={editingCashFlow ?? undefined}
          accounts={accounts ?? []}
          categories={categories}
          onClose={() => {
            setShowCreateForm(false)
            setEditingCashFlow(null)
          }}
          onSuccess={() => {
            setShowCreateForm(false)
            setEditingCashFlow(null)
          }}
        />
      )}

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      ) : cashFlows.length === 0 ? (
        // Requirements 4.1, 4.2, 4.3, 4.4 - 使用增强空状态组件
        (() => {
          const hasFilters = Boolean(
            filters.accountId || filters.categoryId || filters.type ||
            filters.startDate || filters.endDate
          )
          const emptyStateType = determineEmptyStateType({
            hasData: false,
            hasFilters,
            hasError: false,
            isFirstVisit: false,
          })

          if (emptyStateType === 'no-results') {
            return (
              <NoResultsEmptyState
                title="未找到匹配的记录"
                description="尝试调整筛选条件查看更多记录"
                onClearFilters={() => {
                  setFilters({ page: 1, pageSize: 20 })
                }}
              />
            )
          }

          return (
            <EmptyState
              type={emptyStateType}
              title="暂无现金流记录"
              description="点击上方按钮创建第一条记录"
              icon={Wallet}
              primaryAction={{
                label: '新建记录',
                onClick: () => setShowCreateForm(true),
              }}
            />
          )
        })()
      ) : (
        <div className="rounded-lg border">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left text-sm font-medium">日期</th>
                <th className="px-4 py-3 text-left text-sm font-medium">类型</th>
                <th className="px-4 py-3 text-left text-sm font-medium">账户</th>
                <th className="px-4 py-3 text-left text-sm font-medium">分类</th>
                <th className="px-4 py-3 text-right text-sm font-medium">金额</th>
                <th className="px-4 py-3 text-left text-sm font-medium">描述</th>
                <th className="px-4 py-3 text-right text-sm font-medium">操作</th>
              </tr>
            </thead>
            <tbody>
              {cashFlows.map((cashFlow) => (
                <tr key={cashFlow.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-3 text-sm">{cashFlow.flowDate}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cashFlow.type === 'income'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                        }`}
                    >
                      {cashFlow.type === 'income' ? (
                        <ArrowUpCircle className="h-3 w-3" />
                      ) : (
                        <ArrowDownCircle className="h-3 w-3" />
                      )}
                      {cashFlow.type === 'income' ? '收入' : '支出'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">{cashFlow.account?.name ?? '-'}</td>
                  <td className="px-4 py-3 text-sm">{cashFlow.category?.name ?? '-'}</td>
                  <td
                    className={`px-4 py-3 text-right font-medium ${cashFlow.type === 'income' ? 'text-green-600' : 'text-red-600'
                      }`}
                  >
                    {cashFlow.type === 'income' ? '+' : '-'}
                    {formatMoney(cashFlow.amount, cashFlow.currency)}
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {cashFlow.description || '-'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => setEditingCashFlow(cashFlow)}
                        className="rounded px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        编辑
                      </button>
                      <button
                        onClick={() => void handleDelete(cashFlow.id)}
                        className="rounded px-2 py-1 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        删除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            共 {meta.total} 条记录，第 {meta.page}/{meta.totalPages} 页
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(meta.page - 1)}
              disabled={meta.page <= 1}
              className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
            >
              上一页
            </button>
            <button
              onClick={() => handlePageChange(meta.page + 1)}
              disabled={meta.page >= meta.totalPages}
              className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
            >
              下一页
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog />
    </div>
  )
}
